import { ModuleFormValuesType } from 'src/components/Settings/AppSetupPage/SetupApp/appSetupTypes';
import {
  ExtensionsSettings,
  ModuleSettingsItem,
  ModuleType,
} from 'src/constants/dataTypes/index';
import {
  contractModule,
  defaultModules,
} from 'src/hooks/usePortalModulesState';
/**
 * This function gets the module icon from the extensions/default
 * module array.
 * @param moduleType
 * @param moduleId
 */
export const getModuleIcon = (
  moduleType: ModuleType,
  moduleId: string,
  extensionsSettings: ExtensionsSettings,
) => {
  if (moduleType === 'module') {
    const module = defaultModules.find((mod) => mod.id === moduleId);
    return module?.iconName;
  }

  const extensionConfigEntry = Object.entries(extensionsSettings || {}).find(
    ([extId]) => extId === moduleId,
  );

  if (extensionConfigEntry) {
    return extensionConfigEntry[1].icon;
  }

  return '';
};

/**
 * This function gets the module label from the extensions/default
 * module array.
 * @param moduleType
 * @param moduleId
 */
export const getModuleName = (
  moduleType: ModuleType,
  moduleId: string,
  extensionsSettings: ExtensionsSettings,
) => {
  if (moduleType === 'module') {
    const module = defaultModules.find((mod) => mod.id === moduleId);
    return module?.label;
  }

  const extensionConfigEntry = Object.entries(extensionsSettings || {}).find(
    ([extId]) => extId === moduleId,
  );

  if (extensionConfigEntry) {
    return extensionConfigEntry[1].name;
  }

  return '';
};

/**
 * Method to generate the updated module list
 * @param moduleSettings list of all the user modules saved
 * @param currentModule this is the original module value before upating
 * @param updatedModuel these are the new values to which module will be updated
 */
export const getUpdatedModuleSettings = ({
  moduleSettings,
  currentModule,
  updatedModule,
}: {
  moduleSettings: ModuleSettingsItem[] | undefined;
  currentModule: ModuleFormValuesType;
  updatedModule: ModuleSettingsItem;
}): ModuleSettingsItem[] => {
  // module settings
  // incase user have no saved module settings
  // fallback to default module settings
  const stagedModuleSettings =
    moduleSettings && moduleSettings.length
      ? moduleSettings
      : defaultModules.map((module) => ({
          id: module.id,
          label: module.title,
          type: module.type,
          icon: module.iconName,
          disabled: false,
        }));

  const updatedModuleSettings = stagedModuleSettings.map((module) => {
    if (module.id === currentModule.id) {
      return {
        id: module.id,
        icon: updatedModule.icon,
        label: updatedModule.label,
        // if module is already disabled then enable the module
        // otherwise pick the updated module value
        disabled: currentModule?.disabled ? false : updatedModule?.disabled,
        type: module.type,
      };
    }

    return module;
  });

  return updatedModuleSettings as ModuleSettingsItem[];
};

/**
 * Retrieves the saved module settings for the user. If the contracts module is not found
 * in the user's saved settings and the EnableContracts flag is turned on, a disabled
 * contract module is added to the settings. This allows the user to add the contract module
 * from the 'Add an app' page. Note: This check  will be unnecessary after
 * running the backfill operation.
 *
 * @returns {ModuleSettingsItem[]} The updated module settings with a disabled contract module
 * if necessary.
 */
export const getSavedModuleSettings = (
  moduleSettings: ModuleSettingsItem[] | undefined,
  EnableContracts: boolean,
): ModuleSettingsItem[] => {
  if (!moduleSettings?.length) return [];

  const hasSavedContractsModule = moduleSettings.find(
    (m) => m.id === contractModule.id,
  );

  // If contracts are enabled and the contracts module is not saved, add a disabled
  // contract module to the settings for the user to enable later.
  if (EnableContracts && !hasSavedContractsModule) {
    return [
      ...moduleSettings,
      {
        id: contractModule.id,
        label: contractModule.title,
        disabled: true,
        icon: contractModule.iconName,
        type: contractModule.type,
      },
    ];
  }

  // Return the original module settings if no modifications are needed.
  return moduleSettings;
};
